import { useMemo, useState } from 'react';
import { Layout, Menu, Drawer } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  CarOutlined,
  TeamOutlined,
  CalendarOutlined,
  WalletOutlined,
  TagOutlined,
  PlusOutlined,
  BarChartOutlined,
  CustomerServiceOutlined,
  EnvironmentOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  BellOutlined,
  SettingOutlined,
  AuditOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { ROUTES } from '@/config/constants';
import BrandLogo from '@/components/common/BrandLogo';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (v: boolean) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const PROMOTIONS_GROUP = 'promotions';

const mainMenuItems: MenuProps['items'] = [
  { key: ROUTES.DASHBOARD, icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: ROUTES.ORDERS, icon: <ShoppingCartOutlined />, label: 'Orders' },
  { key: ROUTES.RESTAURANTS, icon: <ShopOutlined />, label: 'Restaurants' },
  { key: ROUTES.DELIVERY_PARTNERS, icon: <CarOutlined />, label: 'Delivery Partners' },
  { key: ROUTES.CUSTOMERS, icon: <TeamOutlined />, label: 'Customers' },
  { key: ROUTES.SUBSCRIPTIONS, icon: <CalendarOutlined />, label: 'Subscriptions' },
  { key: ROUTES.WALLET_FINANCE, icon: <WalletOutlined />, label: 'Wallet & Finance' },
  {
    key: PROMOTIONS_GROUP,
    icon: <TagOutlined />,
    label: 'Promotions',
    children: [
      { key: ROUTES.PROMOTIONS, label: 'Promotions' },
      { key: ROUTES.NEW_COUPON, icon: <PlusOutlined />, label: 'Create Coupon' },
    ],
  },
  { key: ROUTES.REPORTS, icon: <BarChartOutlined />, label: 'Reports' },
  { key: ROUTES.SUPPORT_TICKETS, icon: <CustomerServiceOutlined />, label: 'Support / Tickets' },
  { key: ROUTES.SERVICE_ZONES, icon: <EnvironmentOutlined />, label: 'Service Zones' },
  { key: ROUTES.ADMINS, icon: <UsergroupAddOutlined />, label: 'Admins' },
  { key: ROUTES.BANNERS_CATEGORIES, icon: <AppstoreOutlined />, label: 'Banners & Categories' },
  { key: ROUTES.NOTIFICATIONS, icon: <BellOutlined />, label: 'Notifications' },
  { key: ROUTES.SETTINGS, icon: <SettingOutlined />, label: 'Settings' },
  { key: ROUTES.INVOICE_GST, icon: <AuditOutlined />, label: 'Invoice & GST' },
];

function selectedMenuKey(pathname: string) {
  if (pathname.startsWith(`${ROUTES.SUPPORT_TICKETS}/`) || pathname === ROUTES.SUPPORT_TICKETS) {
    return ROUTES.SUPPORT_TICKETS;
  }
  if (pathname.startsWith(`${ROUTES.DELIVERY_PARTNERS}/`)) return ROUTES.DELIVERY_PARTNERS;
  if (pathname.startsWith(`${ROUTES.CUSTOMERS}/`)) return ROUTES.CUSTOMERS;
  if (pathname.startsWith(`${ROUTES.SERVICE_ZONES}`)) return ROUTES.SERVICE_ZONES;
  if (pathname.startsWith(`${ROUTES.INVOICE_GST}`)) return ROUTES.INVOICE_GST;
  if (pathname.startsWith(`${ROUTES.SUBSCRIPTIONS}/`) || pathname === ROUTES.SUBSCRIPTIONS) {
    return ROUTES.SUBSCRIPTIONS;
  }
  if (pathname === ROUTES.NEW_COUPON) return ROUTES.NEW_COUPON;
  if (pathname.startsWith(`${ROUTES.PROMOTIONS}/`) || pathname === ROUTES.PROMOTIONS) {
    return ROUTES.PROMOTIONS;
  }
  if (pathname.startsWith(ROUTES.BANNERS_CATEGORIES)) return ROUTES.BANNERS_CATEGORIES;
  if (pathname.startsWith(ROUTES.ADMINS)) return ROUTES.ADMINS;
  return pathname;
}

function isPromotionsPath(pathname: string) {
  return pathname === ROUTES.PROMOTIONS || pathname.startsWith(`${ROUTES.PROMOTIONS}/`);
}

const superAdminItems = [
  { key: ROUTES.OPS_ADMIN, icon: <SafetyOutlined />, label: 'Ops Admin Panel' },
];

function SidebarContent({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedKey = selectedMenuKey(location.pathname);
  const [openKeys, setOpenKeys] = useState<string[]>(
    isPromotionsPath(location.pathname) ? [PROMOTIONS_GROUP] : [],
  );

  const menuOpenKeys = useMemo(() => {
    if (collapsed) return [];
    if (isPromotionsPath(location.pathname) && !openKeys.includes(PROMOTIONS_GROUP)) {
      return [...openKeys, PROMOTIONS_GROUP];
    }
    return openKeys;
  }, [collapsed, location.pathname, openKeys]);

  return (
    <div className="flex flex-col h-full" style={{ background: 'transparent' }}>
      <div
        className={`flex border-b border-white/[0.07] ${
          collapsed ? 'items-center justify-center px-2' : 'flex-col items-start justify-center px-5'
        } py-4`}
        style={{ minHeight: 68 }}
      >
        <BrandLogo
          tone="onDark"
          variant={collapsed ? 'mark' : 'wordmark'}
          height={collapsed ? 36 : 32}
        />
        {!collapsed && (
          <p
            className="text-[10px] font-semibold tracking-[0.18em] uppercase whitespace-nowrap mt-1.5"
            style={{ color: '#6b8cb3' }}
          >
            Admin Portal
          </p>
        )}
      </div>

      {/* Scrollable menu */}
      <div className="flex-1 overflow-y-auto py-2">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={menuOpenKeys}
          onOpenChange={setOpenKeys}
          items={mainMenuItems}
          onClick={({ key }) => {
            if (key === PROMOTIONS_GROUP) return;
            navigate(key);
          }}
          style={{ background: 'transparent', border: 'none' }}
        />

        {/* Super Admin section */}
        <div className="mt-2 border-t border-white/[0.06] pt-2">
          {!collapsed && (
            <p
              className="text-[10px] font-semibold tracking-widest uppercase px-5 pt-3 pb-2"
              style={{ color: '#445566' }}
            >
              Super Admin
            </p>
          )}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={superAdminItems}
            onClick={({ key }) => navigate(key)}
            style={{ background: 'transparent', border: 'none' }}
          />
        </div>
      </div>

      {/* Version tag */}
      {!collapsed && (
        <div className="px-5 py-4 border-t border-white/[0.06]">
          <p className="text-[11px] font-medium" style={{ color: '#6b8cb3' }}>
            v1.0.0 · Betrfood
          </p>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed, onCollapse, mobileOpen, onMobileClose }: SidebarProps) {
  return (
    <>
      {/* Desktop sidebar */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        width={260}
        collapsedWidth={72}
        className="hidden lg:block"
        style={{
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          background: 'linear-gradient(180deg, #07111f 0%, #0b1a30 100%)',
          boxShadow: '8px 0 32px rgba(7, 17, 31, 0.35)',
        }}
      >
        <SidebarContent collapsed={collapsed} />
      </Sider>

      {/* Mobile drawer */}
      <Drawer
        open={mobileOpen}
        onClose={onMobileClose}
        placement="left"
        width={260}
        styles={{ body: { padding: 0, background: '#07111f' }, header: { display: 'none' } }}
        className="lg:hidden"
      >
        <SidebarContent collapsed={false} />
      </Drawer>
    </>
  );
}
